import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ShopProvider, useShop } from "@/context/ShopContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import Boutique from "@/pages/Boutique";
import ProductPage from "@/pages/ProductPage";
import ComingSoon from "@/pages/ComingSoon";
import Idees from "@/pages/Idees";
import About from "@/pages/About";
import Cart from "@/pages/Cart";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import PaymentReturn, { PaymentCancelled } from "@/pages/PaymentReturn";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sid = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api
      .post("/auth/google-session", { session_id: sid })
      .then((r) => {
        setUser(r.data);
        navigate("/compte", { replace: true });
      })
      .catch(() => {
        toast.error("Connexion Google échouée, réessaie.");
        navigate("/compte", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="py-40 text-center font-display italic text-2xl text-kay-ink/40">Connexion en cours…</div>;
}

function MaintenancePage({ message }) {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5" data-testid="maintenance-page">
      <div className="text-center max-w-md">
        <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-6">Maintenance</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">
          Notre boutique se refait <span className="italic text-kay-pink">une beauté.</span>
        </h1>
        <p className="mt-6 text-kay-ink/60 whitespace-pre-line leading-relaxed" data-testid="maintenance-message">{message}</p>
        <Link to="/coming-soon" className="mt-8 inline-block rounded-full bg-kay-ink text-white px-8 py-3.5 text-sm font-semibold hover:bg-kay-pink transition-colors">
          Voir les nouveautés à venir
        </Link>
      </div>
    </div>
  );
}

function CartGate() {
  const { maintenance } = useShop();
  if (maintenance) return <MaintenancePage message={maintenance.message} />;
  return <Cart />;
}

function AppRoutes() {
  const location = useLocation();
  // Détecte le retour Google OAuth AVANT les routes protégées (hash, pas query)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <ScrollTop />
      <Toaster position="top-center" richColors />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/boutique" element={<Boutique key="all" />} />
          <Route path="/boutique/:category" element={<Boutique key={location.pathname} />} />
          <Route path="/produit/:pid" element={<ProductPage key={location.pathname} />} />
          {/* Anciennes URLs → redirections vers les nouvelles pages catégories */}
          <Route path="/maquillage" element={<Navigate to="/boutique/maquillage" replace />} />
          <Route path="/parfums" element={<Navigate to="/boutique/parfums" replace />} />
          <Route path="/sacs" element={<Navigate to="/boutique/sacs" replace />} />
          <Route path="/levres" element={<Navigate to="/boutique/maquillage" replace />} />
          <Route path="/coming-soon" element={<ComingSoon />} />
          <Route path="/idees" element={<Idees />} />
          <Route path="/a-propos" element={<About />} />
          <Route path="/panier" element={<CartGate />} />
          <Route path="/paiement/retour" element={<PaymentReturn />} />
          <Route path="/paiement/annule" element={<PaymentCancelled />} />
          <Route path="/compte" element={<Account />} />
          <Route path="/admin" element={<Admin />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ShopProvider>
            <AppRoutes />
          </ShopProvider>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
